# The Knowledge Factory

import { Card, CardContent, Figure, PropositionGraphFigure, secondBrainGraph, ToolLauncher } from "./article-components"

<Section index="01" title="Overview">
  <P>
    A customer problem becomes a ticket: evidence compressed, tradeoffs chosen, answer framed. That is the
    knowledge factory. AI accelerates whatever system exists—hidden queues or shared learning. Engineers can
    execute its instructions or become{" "}
    <Term definition="a participant who improves the reusable machinery, context, standards, and feedback loops through which many work items pass.">factory engineers</Term>{" "}
    who improve the line.
  </P>
</Section>

<Section index="02" title="What the Previous Articles Establish">
  <P>This fourth essay builds on three claims:</P>
  <ul>
    <li><strong><ArticleLink slug="vision-and-values">Vision and Values</ArticleLink>:</strong> value begins with human stakes and accountable choice, not output.</li>
    <li><strong><ArticleLink slug="understanding-and-bottlenecks">Understanding and Bottlenecks</ArticleLink>:</strong> clear context lets more people solve the right problems.</li>
    <li><strong><ArticleLink slug="truth-and-inference">Truth and Inference</ArticleLink>:</strong> prediction needs stable constraints and feedback; coherence proves neither truth nor meaning.</li>
  </ul>
  <P>This essay asks what an organization must build once it accepts those claims.</P>
</Section>

<Section index="03" title="Core Thesis">
  <P>
    A knowledge factory turns learning—not models or agents alone—into reusable problem-solving capacity.
  </P>
  <P>
    Factory engineers improve the{" "}
    <Term definition="navigable relationships among people, concepts, systems, evidence, decisions, dependencies, and outcomes, with provenance.">context graph</Term>
    , ontology, workflows, evaluation, observability, and feedback through which future decisions pass. Their unit
    is the capability producing the next hundred outputs.
  </P>
  <Card className="essay-card"><CardContent><P className="essay-card__claim">Learning becomes reusable capital; reusable capital becomes problem-solving capacity.</P></CardContent></Card>
</Section>

<Section index="04" title="Key Terms">
  <Asset id="glossary-cards" />
</Section>

<Section index="05" title="1. You Already Have a Factory">
  <P>
    Customer experience → evidence → interpretation → priority → design → implementation → verification →
    release → observed consequence. That path is already a factory.
  </P>
  <Figure caption="How one product change becomes a production system"><Asset id="product-pipeline" /></Figure>
  <Card className="essay-card"><CardContent><P className="essay-card__claim">The factory is not the tools. It is the chain of handoffs that decides what survives.</P></CardContent></Card>
  <P>
    This system has queues, handoffs, stations, checks, rework, bottlenecks, and feedback. Its design decides what
    survives, who may question the plan, and where learning goes. AI amplifies clear context or vague tickets,
    shared learning or fragmented memory, serious evaluation or cosmetic approval. The factory was always there;
    AI makes its shape consequential faster.
  </P>
</Section>

<Section index="06" title="2. The Implicit Factory Turns People into Workers">
  <P>
    A small group defines solutions; tickets move downstream; customer context fades; engineers optimize locally.
    Success becomes output and schedule; learning stays in calls, pull requests, and memory.
  </P>
  <Figure caption="Two operating models for the same factory"><Asset id="implicit-vs-explicit-factory" /></Figure>
  <P>
    An implicit factory keeps queues hidden and decisions gated; an explicit factory makes context, evaluation,
    and feedback visible. Talented people become bounded workers because they cannot improve the frame or system.
  </P>
</Section>

<Section index="07" title="3. A Factory Engineer Improves the Line">
  <P>A factory engineer improves a class of outputs, not one unit:</P>
  <ul>
    <li>aligning a domain concept across prompts, schemas, APIs, analytics, and UI;</li>
    <li>turning repeated review judgment into an evaluation suite;</li>
    <li>connecting decisions to evidence and outcomes;</li>
    <li>replacing coordination queues with safe self-service;</li>
    <li>making agent failures visible and learnable;</li>
    <li>encoding side effects and escalation boundaries; and</li>
    <li>letting domain experts change systems without routing everything through specialists.</li>
  </ul>
  <Figure caption="Completing one unit versus improving the capability that produces many"><Asset id="worker-vs-factory-engineer" /></Figure>
  <P>
    This combines domain knowledge, systems thinking, software craft, teaching, and institutional design. It is a
    way of working—not a title—across product, research, operations, design, engineering, and leadership.
  </P>
</Section>

<Section index="08" title="4. Make Good Judgment Portable">
  <P>
    With the same models, a gated company sends framed solutions downstream; AI burdens the gate. A distributed
    company gives teams evidence, context, boundaries, tools, and evaluations to test locally and escalate only
    broader-authority choices.
  </P>
  <P>
    The latter explores more without lowering standards. This{" "}
    <Term definition="framing, generating, testing, and revising interventions in response to a meaningful problem.">distributed solutioning</Term>{" "}
    depends on context, decision rights, safety constraints, and evaluation—not unlimited autonomy.
  </P>
</Section>

<Section index="09" title="5. Fix the Factory Before Asking AI to Scale It">
  <P>
    AI cannot preserve unnamed boundaries, reconcile conflicting contracts, or verify remembered correctness; it
    consumes structural debt. Make contracts machine-readable and carry domain distinctions across database, API,
    runtime validation, application types, analytics, and UI.
  </P>
  <ul>
    <li>schemas can generate types, validators, clients, fixtures, and documentation;</li>
    <li>API specifications can generate request and response types, stubs, and clients;</li>
    <li>database schemas can generate query types, migrations, and policy checks; and</li>
    <li>design systems can generate tokens, components, documentation, and visual references.</li>
  </ul>
  <P>
    The source need not be a particular technology. It must be authoritative enough to own, version, validate,
    and regenerate. Generated artifacts are materialized views, not competing truths.
  </P>
  <P className="article-outline__flow">Authoritative source → generated contracts → runtime validation → end-to-end verification.</P>
  <P>
    Types expose relationships; runtime checks guard untyped boundaries; tests prove composition. Together they
    make changes legible enough for AI to propose, check, and correct.
  </P>
</Section>

<Section index="10" title="6. Systematize the Whole Chain">
  <P>Software is one station. Repeated decisions across the chain can become reusable systems:</P>
  <ul>
    <li><strong>design:</strong> shared tokens, components, interaction rules, accessibility checks, and visual regression evidence;</li>
    <li><strong>reports:</strong> governed definitions, datasets, queries, templates, provenance, and scheduled review;</li>
    <li><strong>engagement:</strong> research repositories, support signals, experiments, consent, segmentation, and feedback loops;</li>
    <li><strong>development:</strong> schemas, types, tests, build pipelines, release controls, observability, and incident learning; and</li>
    <li><strong>operations:</strong> explicit workflows, ownership, service levels, escalation paths, and outcome measures.</li>
  </ul>
  <Quote><strong>Systematize everything that repeats.</strong> This does not mean automate every decision.</Quote>
  <P>A system may end in human judgment while preserving inputs, constraints, choice, and consequences for the next cycle.</P>
</Section>

<Section index="11" title="7. The Knowledge-Factory Stack">
  <P>The eight-layer stack is an inventory, not a vendor architecture.</P>
  <Figure caption="Eight reusable layers, from observation through learning"><Asset id="knowledge-factory-stack" /></Figure>
  <P>
    In AI-assisted mathematics, a problem and its literature supply context; an orchestrator and specialist agents
    generate conjectures, lemmas, counterexamples, scripts, and proofs; tests or proof assistants reject candidates;
    provenance records tools and assumptions. Mathematicians still judge fidelity, significance, and direction.
    High-volume search helps only with trustworthy verification and human governance of meaning, standards,
    attribution, and direction.
  </P>
</Section>

<Section index="12" title="8. Triggers Open Investigations">
  <P>
    A product metric crosses a threshold after a release. PostHog reports a journey drop, Sentry reports errors,
    and runtime telemetry supplies operating conditions. These observations justify investigation; they do not
    establish a cause or authorize a code change. This is an illustrative workflow assembled from documented
    sensing capabilities, not a demonstrated autonomous product.
  </P>
  <P>A trigger should open a hypothesis, not declare a diagnosis.</P>
  <P>Translate the signal into an event contract containing:</P>
  <ul>
    <li>source, observation window, baseline, provenance, and uncertainty;</li>
    <li>affected journey, system, release, and owner;</li>
    <li>corroborating and contradicting evidence; and</li>
    <li>permitted next steps, expiry, and links to the underlying observations.</li>
  </ul>
  <P>
    Keep several explanations live: a regression, an intended change with an unexpected tradeoff, or a coincident
    change elsewhere. Name the observation that could distinguish them. A work record links these hypotheses,
    their evidence, and the person responsible for the next decision.
  </P>
  <Asset id="trigger-opens-hypotheses-motif" />
  <P>
    Configure thresholds, event identity, deduplication, and cooldowns so repeated alerts do not launch repeated
    interventions. A trigger may schedule an investigation; the evidence and authority required for a release
    remain separate. Rules handle exact conditions. Model judgment can help classify bounded ambiguity, while
    contested goals and exceptions go to the responsible people.
  </P>
</Section>

<Section index="13" title="9. Agent DAGs Make Delegation Inspectable">
  <P>
    Authorized work becomes a directed acyclic graph (DAG) of scoped tasks. For one investigation, context
    retrieval and failure reproduction can proceed independently, converge on a hypothesis review, and then
    feed a proposed change, verification, and an integration decision.
  </P>
  <P>Each node names:</P>
  <ul>
    <li>inputs, selectively retrieved context, and expected outputs;</li>
    <li>tools, permissions, and permitted side effects;</li>
    <li>dependencies, acceptance checks, and evidence to retain;</li>
    <li>an owner and the authority required for the next handoff; and</li>
    <li>retry limits, stop conditions, and recovery or rollback requirements.</li>
  </ul>
  <P>
    The DAG describes dependencies within one work attempt. The feedback loop surrounds that attempt:
    new evidence can schedule a revised graph or a bounded retry. A loop across attempts does not make
    the task graph cyclic. Record state durably so interruption or duplicate delivery does not silently
    repeat a side effect.
  </P>
  <P>
    Give teams authority to revise local work within their boundary. An integration owner resolves shared
    dependencies without becoming the mandatory reviewer of every local decision. Escalate changes that alter
    another team's commitments or exceed the task's mandate.
  </P>
  <P>The graph—not the model—is the durable unit of automation.</P>
  <P>
    Here that means the inspectable work record preserves decisions, dependencies, and outcomes beyond an
    individual model call. It is an architectural proposal, not a claim about a particular orchestration product.
  </P>
  <Asset id="executable-context-card" />
  <P>
    Executable context turns relevant knowledge into enforceable checks: a definition becomes a schema,
    an ownership rule an escalation path, and an observed failure a regression case. Historical records remain
    evidence to consult; they become operating constraints only through an explicit, owned decision.
  </P>
</Section>

<Section index="14" title="10. Engineer the Return Path">
  <Figure caption="A proposed factory control loop. Observations enter through a contract; work proceeds through explicit authority and verification; consequences return as evidence.">
    <Asset id="factory-control-loop" />
  </Figure>
  <P>
    Loop engineering connects a completed action to the consequence it was meant to produce. Before executing,
    name the expected effect, observation window, acceptable tradeoffs, outcome owner, and conditions for stopping
    or reversing the change.
  </P>
  <ol>
    <li>Record a signal and the competing explanations it opens.</li>
    <li>Authorize a bounded investigation and instantiate its task graph.</li>
    <li>Produce a reviewable artifact with evidence and integration ownership.</li>
    <li>Apply checks and the appropriate release authority.</li>
    <li>Observe customer and operational consequences during the stated window.</li>
    <li>Retain the result, revise the hypothesis, and update the relevant evaluation or procedure.</li>
  </ol>
  <P>
    Verification checks the artifact against known requirements. Outcome evaluation asks whether the action
    produced the intended effect. A passing test can coexist with a worse customer journey; a recovered funnel
    can conceal higher support cost.
  </P>
  <P>
    Bound retries and escalation, define what happens when evidence never arrives, and make delayed outcomes
    visible. Close the work as inconclusive when warranted. Repeated generation without a stopping rule is
    an unfinished process, not a learning loop.
  </P>
  <P className="article-outline__flow">
    Work produces outcomes → outcomes produce evidence → evidence updates context and evaluation →
    better context can improve the next work.
  </P>
</Section>

<Section index="15" title="11. Human Direction Sets the Boundary">
  <P>
    A factory retrieves evidence, generates options, exposes inconsistencies, and simulates reactions. It cannot
    choose the future or whose outcome counts. Evidence narrows the choice; a person makes the accountable wager.
  </P>
  <P>Do not hide judgment behind automation; make it inspectable. For each consequential choice, retain:</P>
  <ul>
    <li>the desired change and whose experience defines its stakes;</li>
    <li>supporting and contradictory evidence;</li>
    <li>assumptions, uncertainty, and rejected alternatives;</li>
    <li>owners, decision rights, and escalation boundaries;</li>
    <li>predictions and disconfirming signals; and</li>
    <li>the revision made after consequences arrive.</li>
  </ul>
  <Quote><strong>Systematize the feedback. Do not automate away the judgment.</strong></Quote>
</Section>

<Section index="16" title="12. Retain Learning, Not Just Outputs">
  <P>The factory compounds only when completed work changes the context available to the next decision:</P>
  <P className="article-outline__flow">Evidence → interpretation → choice → action → outcome → revised context.</P>
  <Figure caption="Work becomes reusable when outcomes revise the next decision's context."><Asset id="feedback-loop-figure" /></Figure>
  <P>
    Organizational memory is more than notes: it links claims to evidence, decisions to owners, experiments to
    predictions, and outcomes to revisions. Search finds documents; graph context recovers reasoning, dependencies,
    and needed revisions.
  </P>
  <P>It answers: Why did this matter? What supports or contradicts it? What depends on it? What did we predict? What makes us stop or revise? What did we learn?</P>
  <Figure caption="Organizational memory connects evidence, decisions, actors, experiments, metrics, and outcomes.">
    <PropositionGraphFigure document={secondBrainGraph} title="The organizational memory graph" />
  </Figure>
  <P><ToolLauncher toolId="relationship-graph" href="/relationship-graph" label="Explore the relationship graph" />{" "}or open the <BlogLink href="/relationship-graph">relationship graph editor</BlogLink>.</P>
</Section>

<Section index="17" title="13. Start with One Broken Handoff">
  <P>Do not begin with a company-wide AI program. Start where context disappears or judgment is trapped in a review queue:</P>
  <ol>
    <li>Trace customer experience to observed consequence.</li>
    <li>Expose hidden evidence and decisions.</li>
    <li>Name stable distinctions and invariants.</li>
    <li>Turn repeated judgment into tools, workflows, tests, and escalation rules.</li>
    <li>Let teams frame and test solutions within those boundaries.</li>
    <li>Instrument outcomes and connect them to the original decision.</li>
    <li>Promote validated learning into shared context.</li>
  </ol>
  <P>The goal is more sound judgment, responsible experiments, and consequences returned to memory—not maximum automation.</P>
</Section>

<Section index="18" title="14. What Actually Compounds">
  <P>
    Durable advantage is the residue of learning. Proprietary data, domain knowledge, ontology, tools, relationships,
    infrastructure, and network effects become defensible as one customer-value system that improves through use.
    Owning the parts is not the moat. Compounding them is.
  </P>
</Section>

<Section index="19" title="15. Ontology Makes It Coherent; Cognition Makes It Learn">
  <P>
    Two disciplines complete the model. The <ArticleLink slug="the-ontology-factory">Ontology Factory</ArticleLink>{" "}
    makes ownership, vocabulary, relationships, constraints, and evidence rules checkable. The{" "}
    <ArticleLink slug="the-cognitive-factory">Cognitive Factory</ArticleLink> develops the sensemaking capabilities that guide this machinery: meaningful signals, cognitive reach, and
    discoverable memory of plans, priorities, decisions, and outcomes. Ontology makes those records interpretable;
    cognition relates them to the present problem; people choose what is worth pursuing.
  </P>
</Section>

<div className="essay-closing"><blockquote>The companies that win will let teams see the whole line, learn from its consequences, and improve the factory—not merely turn engineers into faster workers.</blockquote></div>

<Section index="20" title="Sources">
  <ul>
    <li>Sentry, <ExternalLink href="https://docs.sentry.io/product/ai-in-sentry/seer/">Seer documentation</ExternalLink>; PostHog, <ExternalLink href="https://posthog.com/docs/product-analytics/insights/">Insights documentation</ExternalLink>; AWS, <ExternalLink href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/alarm-actions.html">CloudWatch alarm actions</ExternalLink>. Individual sensing and investigation capabilities; the workflow composition is proposed here.</li>
    <li>DORA, Google, <ExternalLink href="https://research.google/pubs/dora-2025-state-of-ai-assisted-software-development-report/"><em>2025 State of AI-assisted Software Development Report</em></ExternalLink>. AI as a systems problem amplifying strengths and weaknesses.</li>
    <li>Ikujiro Nonaka, <ExternalLink href="https://doi.org/10.1287/orsc.5.1.14">“A Dynamic Theory of Organizational Knowledge Creation”</ExternalLink> (1994). Continuous knowledge creation as shared capability.</li>
    <li>James G. March, <ExternalLink href="https://doi.org/10.1287/orsc.2.1.71">“Exploration and Exploitation in Organizational Learning”</ExternalLink> (1991). Exploration versus capability refinement.</li>
    <li>Karl E. Weick, Kathleen M. Sutcliffe, and David Obstfeld, <ExternalLink href="https://doi.org/10.1287/orsc.1050.0133">“Organizing and the Process of Sensemaking”</ExternalLink> (2005). Equivocal evidence and provisional models.</li>
    <li>James P. Walsh and Gerardo Rivera Ungson, <ExternalLink href="https://doi.org/10.5465/AMR.1991.4278992">“Organizational Memory”</ExternalLink> (1991). Memory acquisition, retention, retrieval, use, and misuse.</li>
    <li>Michael E. Porter, <ExternalLink href="https://hbr.org/1996/11/what-is-strategy">“What Is Strategy?”</ExternalLink> (1996). Coherent choices and activities, not improvement lists.</li>
    <li>ISO, <ExternalLink href="https://www.iso.org/standard/77520.html"><em>ISO 9241-210:2019</em></ExternalLink>. Ongoing attention to users, needs, and consequences.</li>
    <li>NIST, <ExternalLink href="https://doi.org/10.6028/NIST.AI.100-1"><em>AI Risk Management Framework 1.0</em></ExternalLink> (2023). Context mapping, measurement, evaluation, governance, and accountability.</li>
  </ul>
</Section>
